<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { ErrorState, ActionButton, StatusTag } from '@taler/ui'
import {
  absMoney,
  equalsMoney,
  formatDate,
  formatInstantDate,
  formatInstantTimeUtc,
  formatLocalDate,
  formatMoney,
  type Instant,
  isCurrencyCode,
  type Money,
  parseLocalDate,
} from '@taler/domain'
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { flowMoney, signTone, useAccountingView } from '@/app/accounting-view'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker } from '@/features/entries'
import CrossLedgerLinkBadge from '@/features/transactions/components/CrossLedgerLinkBadge.vue'
import KindTile from '@/features/transactions/components/KindTile.vue'
import {
  type EntryDetailView,
  type EntryDraft,
  type PostableAccountOption,
  type PostingDetail,
  toPostingDraft,
} from '../internal/entry-detail.ts'
import { convertedFigure } from '../internal/transaction-row.ts'
import type { CrossLedgerLink } from '../composables/use-cross-ledger-links.ts'

const props = defineProps<{
  entry: EntryDetailView | undefined
  crossLedgerLink?: CrossLedgerLink | undefined
  postingAccountOptions: readonly PostableAccountOption[]
  isLoading: boolean
  error: Error | null
  retrying?: boolean
  canEdit: boolean
  canReverse: boolean
  canDelete: boolean
  isEditing: boolean
  isSaving: boolean
  saveErrorDetail: string | undefined
  saveErrorPending: boolean
  isReversing: boolean
  reverseErrorDetail: string | undefined
  reverseErrorPending: boolean
  isDeleting: boolean
  deleteErrorDetail: string | undefined
  deleteErrorPending: boolean
}>()

const emit = defineEmits<{
  close: []
  retry: []
  edit: []
  cancelEdit: []
  save: [draft: EntryDraft]
  reverse: []
  delete: []
  followCrossLedgerLink: [CrossLedgerLink]
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const FIELD_CLASS =
  'min-h-[var(--density-control)] w-full rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] aria-[invalid=true]:border-[color:var(--color-danger)]'
const LABEL_CLASS = 'text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]'
const INSPECTOR_LABEL_CLASS =
  'text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]'
const BUTTON_CLASS =
  'min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)] disabled:opacity-60'

interface EditableRow {
  key: number
  accountId: string
  amount: Money | null
  currency: string
  memo: string
}

interface DraftState {
  date: string
  payee: string
  memo: string
  notes: string
  tags: string
  rows: EditableRow[]
}

let rowKeySequence = 0
const draftDate = ref('')
const draftPayee = ref('')
const draftMemo = ref('')
const draftNotes = ref('')
const draftTags = ref('')
const draftRows = ref<EditableRow[]>([])
const submitAttempted = ref(false)
const baseline = ref<DraftState>()
const pendingLeave = shallowRef<() => void>()
const keepEditingButton = ref<HTMLButtonElement>()

function draftState(): DraftState {
  return {
    date: draftDate.value,
    payee: draftPayee.value,
    memo: draftMemo.value,
    notes: draftNotes.value,
    tags: draftTags.value,
    rows: draftRows.value.map((row) => ({ ...row })),
  }
}

function sameRow(a: EditableRow, b: EditableRow): boolean {
  return (
    a.accountId === b.accountId &&
    a.currency === b.currency &&
    a.memo === b.memo &&
    (a.amount === null || b.amount === null ? a.amount === b.amount : equalsMoney(a.amount, b.amount))
  )
}

function sameDraft(a: DraftState, b: DraftState): boolean {
  return (
    a.date === b.date &&
    a.payee === b.payee &&
    a.memo === b.memo &&
    a.notes === b.notes &&
    a.tags === b.tags &&
    a.rows.length === b.rows.length &&
    a.rows.every((row, index) => sameRow(row, b.rows[index]!))
  )
}

const dirty = computed(() => baseline.value !== undefined && !sameDraft(baseline.value, draftState()))

function rowCurrency(row: EditableRow) {
  return isCurrencyCode(row.currency) ? row.currency : undefined
}

function resetDraft(entry: EntryDetailView): void {
  draftDate.value = formatLocalDate(entry.date)
  draftPayee.value = entry.payee ?? ''
  draftMemo.value = entry.memo
  draftNotes.value = entry.notes ?? ''
  draftTags.value = entry.tags.join(', ')
  draftRows.value = entry.postings
    .filter((posting) => !posting.isResidual)
    .map((posting) => {
      rowKeySequence += 1
      const draft = toPostingDraft(posting)
      return {
        key: rowKeySequence,
        accountId: draft.accountId,
        amount: draft.amount,
        currency: draft.currency,
        memo: draft.memo ?? '',
      }
    })
  submitAttempted.value = false
  baseline.value = draftState()
}

watch(
  () => props.isEditing,
  (editing) => {
    if (editing && props.entry !== undefined) resetDraft(props.entry)
  },
  { immediate: true },
)

watch(pendingLeave, async (pending) => {
  if (pending === undefined) return
  await nextTick()
  keepEditingButton.value?.focus()
})

const unsavedEdit = computed(() => props.isEditing && dirty.value)

watch(
  () => props.isEditing,
  (editing) => {
    if (!editing) pendingLeave.value = undefined
  },
)

function warnBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault()
}

watch(unsavedEdit, (unsaved) => {
  if (unsaved) window.addEventListener('beforeunload', warnBeforeUnload)
  else window.removeEventListener('beforeunload', warnBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warnBeforeUnload)
})

function requestLeave(proceed: () => void): void {
  if (props.isSaving) return
  if (unsavedEdit.value) pendingLeave.value = proceed
  else proceed()
}

function keepEditing(): void {
  pendingLeave.value = undefined
}

async function discardChanges(): Promise<void> {
  const proceed = pendingLeave.value
  pendingLeave.value = undefined
  emit('cancelEdit')
  if (proceed === undefined) return
  await nextTick()
  proceed()
}

defineExpose({ requestLeave })

interface RowErrors {
  account: string | undefined
  amount: string | undefined
  currency: string | undefined
}

const dateError = computed(() => (draftDate.value === '' ? t('transactions.detail.dateRequired') : undefined))
const memoError = computed(() =>
  draftMemo.value.trim() === '' ? t('transactions.detail.memoRequired') : undefined,
)

function rowErrors(row: EditableRow): RowErrors {
  return {
    account: row.accountId === '' ? t('transactions.detail.accountRequired') : undefined,
    amount: row.amount === null ? t('transactions.detail.amountRequired') : undefined,
    currency: isCurrencyCode(row.currency) ? undefined : t('transactions.detail.currencyInvalid'),
  }
}

const hasErrors = computed(
  () =>
    dateError.value !== undefined ||
    memoError.value !== undefined ||
    draftRows.value.some((row) => Object.values(rowErrors(row)).some((error) => error !== undefined)),
)

function visibleError(error: string | undefined): string | undefined {
  return submitAttempted.value ? error : undefined
}

function addRow(): void {
  rowKeySequence += 1
  const fallbackCurrency = props.entry?.ownCurrency ?? ''
  draftRows.value = [
    ...draftRows.value,
    { key: rowKeySequence, accountId: '', amount: null, currency: fallbackCurrency, memo: '' },
  ]
}

function removeRow(key: number): void {
  draftRows.value = draftRows.value.filter((row) => row.key !== key)
}

function chooseAccount(row: EditableRow, accountId: string): void {
  row.accountId = accountId
  const option = props.postingAccountOptions.find((candidate) => candidate.id === accountId)
  if (option?.currency !== undefined) row.currency = option.currency
}

function submit(): void {
  submitAttempted.value = true
  if (hasErrors.value) return
  const draft: EntryDraft = {
    date: parseLocalDate(draftDate.value),
    memo: draftMemo.value,
    tags: draftTags.value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0),
    postings: draftRows.value.map((row) => ({
      accountId: row.accountId,
      amount: row.amount,
      currency: row.currency,
      ...(row.memo === '' ? {} : { memo: row.memo }),
    })),
    ...(draftPayee.value === '' ? {} : { payee: draftPayee.value }),
    ...(draftNotes.value === '' ? {} : { notes: draftNotes.value }),
  }
  emit('save', draft)
}

const { accountingView } = useAccountingView()

/** The entry's own-currency figure for a posting shown converted; absent when it is not. */
function nativePostingText(posting: PostingDetail): string | undefined {
  if (convertedFigure(posting.amount, posting.displayAmount) === undefined) return undefined
  const isAccounting = accountingView.value
  const shown = isAccounting ? posting.amount : absMoney(posting.amount)
  return formatMoney(shown, { locale: currentLocale.value, sign: isAccounting })
}

/** Plain view: what the leg did for the person, as a magnitude with a direction word. */
function plainPosting(posting: PostingDetail): { role: string; amount: Money } {
  const kind = props.entry?.kind
  const negative = posting.amount.units < 0n
  const role =
    posting.accountClass === 'EXPENSE'
      ? 'spentOn'
      : posting.accountClass === 'INCOME'
        ? 'earnedFrom'
        : kind === 'EXPENSE'
          ? 'paidFrom'
          : kind === 'INCOME'
            ? 'paidInto'
            : negative
              ? 'movedFrom'
              : 'movedTo'
  return {
    role: t(`transactions.detail.plain.${role}`),
    amount: absMoney(posting.displayAmount ?? posting.amount),
  }
}

const postingsHeading = computed(() => {
  if (systemManaged.value) return t('transactions.detail.revaluation.changesTitle')
  return accountingView.value ? t('transactions.detail.postingsTitle') : t('transactions.detail.plainTitle')
})

const kindLabel = computed(() => {
  if (props.entry?.isRevaluation === true) return t('transactions.revaluation.title')
  return props.entry?.kind === undefined
    ? t('transactions.noKind')
    : t(`transactions.${props.entry.kind.toLowerCase()}`)
})
const dateLabel = computed(() =>
  props.entry === undefined ? '' : formatDate(props.entry.date, currentLocale.value, 'long'),
)
const instantText = (value: Instant): string =>
  `${formatInstantDate(value, currentLocale.value, 'long')}, ${formatInstantTimeUtc(value)}`
// A job revaluation is system-managed: no edit, reverse or delete from its detail.
const systemManaged = computed(() => props.entry?.isRevaluation === true)
const magnitudeOnly = computed(() => props.entry?.kind === 'TRANSFER' && !systemManaged.value)
const totalAmount = computed(() => {
  const entry = props.entry
  if (entry?.displayTotal === undefined) return undefined
  return systemManaged.value
    ? entry.displayTotal
    : flowMoney(entry.kind, entry.displayTotal, entry.categorySign)
})
const totalTone = computed(() => {
  if (totalAmount.value === undefined) return undefined
  return magnitudeOnly.value ? 'transfer' : signTone(totalAmount.value)
})
</script>

<template>
  <header
    class="flex items-start justify-between gap-[var(--space-cell)] px-[var(--space-block)] pt-[var(--space-block)]"
  >
    <div class="flex min-w-0 items-center gap-[var(--space-cell)]">
      <KindTile v-if="entry" :kind="entry.kind" :label="kindLabel" />
      <div class="flex min-w-0 flex-col gap-[var(--space-hair)]">
        <h2 class="truncate text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ entry ? entry.title : t('transactions.detail.title') }}
        </h2>
        <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ entry ? `${kindLabel} · ${dateLabel}` : '' }}
        </p>
      </div>
    </div>
    <button type="button" :class="BUTTON_CLASS" @click="requestLeave(() => emit('close'))">
      {{ t('transactions.detail.close') }}
    </button>
  </header>

  <div class="flex flex-col gap-[var(--space-block)] p-[var(--space-block)]">
    <p v-if="isLoading" role="status" class="text-[color:var(--color-text-muted)]">
      {{ t('transactions.detail.loading') }}
    </p>

    <ErrorState
      v-else-if="error"
      :title="t('transactions.detail.errorTitle')"
      :retry-label="t('transactions.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <template v-else-if="entry">
      <template v-if="!isEditing">
        <section class="flex flex-col items-start gap-[var(--space-inline)]">
          <MoneyAmount
            v-if="totalAmount"
            size="hero"
            :tone="totalTone"
            :value="totalAmount"
            :sign="!magnitudeOnly"
          />
          <p v-else-if="entry.displayTotal === undefined" class="text-[color:var(--color-text-muted)]">—</p>
          <StatusTag v-if="entry.isReversal" tone="neutral">
            {{ t('transactions.reversed') }}
          </StatusTag>
          <CrossLedgerLinkBadge
            v-if="crossLedgerLink"
            :link="crossLedgerLink"
            @follow="emit('followCrossLedgerLink', $event)"
          />
        </section>

        <section class="flex flex-col gap-[var(--space-inline)] text-[length:var(--text-step-2)]">
          <div v-if="entry.memo" class="flex flex-col gap-[var(--space-tight)]">
            <h3 :class="INSPECTOR_LABEL_CLASS">{{ t('transactions.detail.memoLabel') }}</h3>
            <p
              class="rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
            >
              {{ entry.memo }}
            </p>
          </div>
          <p v-if="entry.notes" class="text-[color:var(--color-text)]">{{ entry.notes }}</p>
          <div v-if="entry.tags.length > 0" class="flex flex-col gap-[var(--space-tight)]">
            <h3 :class="INSPECTOR_LABEL_CLASS">{{ t('transactions.filters.tags') }}</h3>
            <ul class="flex flex-wrap gap-[var(--space-tight)]">
              <li
                v-for="tag in entry.tags"
                :key="tag"
                class="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-inline)] text-[color:var(--color-text)]"
              >
                {{ tag }}
              </li>
            </ul>
          </div>
        </section>

        <section class="flex flex-col gap-[var(--space-inline)]">
          <h3
            class="flex items-center gap-[var(--space-inline)] text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]"
          >
            {{ postingsHeading }}
            <span
              v-if="!systemManaged"
              class="rounded-full bg-[color:var(--color-surface-sunken)] px-[var(--space-inline)] text-[length:var(--text-step-1)] font-semibold text-[color:var(--color-text-muted)] tabular-nums"
              >{{ entry.postings.length }}</span
            >
          </h3>
          <p
            v-if="systemManaged"
            class="text-[length:var(--text-step-1)] italic text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.detail.revaluation.noMoneyMoved') }}
          </p>
          <p
            v-if="entry.displayRateMissing"
            class="text-[length:var(--text-step-1)] italic text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.noRateForDate') }}
          </p>
          <ul
            class="flex flex-col divide-y divide-[color:var(--color-border)] rounded-[var(--radius-control)] border border-[color:var(--color-border)]"
          >
            <li
              v-for="posting in entry.postings"
              :key="posting.id"
              class="flex flex-col gap-[var(--space-hair)] px-[var(--space-cell)] py-[var(--space-inline)]"
            >
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <span
                  class="min-w-0 truncate text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
                  :title="posting.account.path"
                >
                  {{ posting.account.leaf }}
                </span>
                <span class="flex shrink-0 items-baseline gap-[var(--space-inline)]">
                  <span
                    v-if="!accountingView && !systemManaged"
                    class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
                    >{{ plainPosting(posting).role }}</span
                  >
                  <span class="w-40">
                    <MoneyAmount
                      v-if="systemManaged && posting.valueChange !== undefined"
                      :value="posting.valueChange"
                      sign
                    />
                    <span v-else-if="systemManaged">—</span>
                    <MoneyAmount
                      v-else-if="accountingView"
                      :value="posting.displayAmount ?? posting.amount"
                      sign
                    />
                    <MoneyAmount v-else :value="plainPosting(posting).amount" />
                    <span
                      v-if="!systemManaged && nativePostingText(posting) !== undefined"
                      class="block text-right text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
                      >{{ nativePostingText(posting) }}</span
                    >
                  </span>
                </span>
              </div>
              <p
                v-if="posting.memo"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
              >
                {{ posting.memo }}
              </p>
              <template v-if="posting.isResidual">
                <p
                  class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-attention)]"
                >
                  {{ t('transactions.detail.residualBadge') }}
                </p>
                <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                  {{ t('transactions.detail.residualExplanation') }}
                </p>
              </template>
            </li>
          </ul>
        </section>

        <section
          v-if="!systemManaged"
          class="flex flex-col gap-[var(--space-inline)] border-t border-[color:var(--color-border)] pt-[var(--space-block)]"
        >
          <h3 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
            {{ t('transactions.detail.detailsTitle') }}
          </h3>
          <dl
            class="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-[var(--space-group)] gap-y-[var(--space-inline)] text-[length:var(--text-step-2)]"
          >
            <dt :class="INSPECTOR_LABEL_CLASS">{{ t('transactions.detail.typeLabel') }}</dt>
            <dd class="text-[color:var(--color-text)]">{{ kindLabel }}</dd>
            <dt :class="INSPECTOR_LABEL_CLASS">{{ t('transactions.detail.dateLabel') }}</dt>
            <dd class="text-[color:var(--color-text)]">{{ dateLabel }}</dd>
            <dt :class="INSPECTOR_LABEL_CLASS">{{ t('transactions.detail.createdLabel') }}</dt>
            <dd class="text-[color:var(--color-text)] tabular-nums">{{ instantText(entry.createdAt) }}</dd>
            <dt :class="INSPECTOR_LABEL_CLASS">{{ t('transactions.detail.modifiedLabel') }}</dt>
            <dd class="text-[color:var(--color-text)] tabular-nums">{{ instantText(entry.updatedAt) }}</dd>
          </dl>
        </section>

        <div v-if="canEdit && !systemManaged" class="flex">
          <ActionButton type="button" @click="emit('edit')">
            {{ t('transactions.detail.editAction') }}
          </ActionButton>
        </div>

        <section
          v-if="canReverse && !systemManaged"
          class="flex flex-col items-start gap-[var(--space-inline)] border-t border-[color:var(--color-border)] pt-[var(--space-block)]"
        >
          <h3 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
            {{ t('transactions.detail.reverseTitle') }}
          </h3>
          <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
            {{ t('transactions.detail.reverseConsequence') }}
          </p>
          <p
            v-if="reverseErrorPending"
            class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.detail.pendingRetry') }}
          </p>
          <StatusTag v-else-if="reverseErrorDetail" tone="danger">{{ reverseErrorDetail }}</StatusTag>
          <button type="button" :disabled="isReversing" :class="BUTTON_CLASS" @click="emit('reverse')">
            {{ isReversing ? t('transactions.detail.reversing') : t('transactions.detail.reverseAction') }}
          </button>
        </section>

        <section
          v-if="canDelete && !systemManaged"
          class="flex flex-col items-start gap-[var(--space-inline)] border-t border-[color:var(--color-border)] pt-[var(--space-block)]"
        >
          <h3 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
            {{ t('transactions.detail.deleteTitle') }}
          </h3>
          <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
            {{ t('transactions.detail.deleteConsequence') }}
          </p>
          <p
            v-if="deleteErrorPending"
            class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
          >
            {{ t('transactions.detail.pendingRetry') }}
          </p>
          <StatusTag v-else-if="deleteErrorDetail" tone="danger">{{ deleteErrorDetail }}</StatusTag>
          <button
            type="button"
            :disabled="isDeleting"
            :class="[BUTTON_CLASS, 'text-[color:var(--color-danger)]']"
            @click="emit('delete')"
          >
            {{ isDeleting ? t('transactions.detail.deleting') : t('transactions.detail.deleteAction') }}
          </button>
        </section>
      </template>

      <form v-else novalidate @submit.prevent="submit">
        <fieldset :disabled="isSaving" class="flex min-w-0 flex-col gap-[var(--space-block)]">
          <section class="grid grid-cols-[10rem_minmax(0,1fr)] gap-[var(--space-group)]">
            <div class="flex flex-col gap-[var(--space-hair)]">
              <label :class="LABEL_CLASS" for="entry-date">{{ t('transactions.detail.dateLabel') }}</label>
              <input
                id="entry-date"
                v-model="draftDate"
                type="date"
                :class="FIELD_CLASS"
                :aria-invalid="visibleError(dateError) !== undefined"
                aria-describedby="entry-date-error"
              />
              <p
                v-if="visibleError(dateError)"
                id="entry-date-error"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
              >
                {{ dateError }}
              </p>
            </div>
            <div class="flex flex-col gap-[var(--space-hair)]">
              <label :class="LABEL_CLASS" for="entry-payee">{{ t('transactions.detail.payeeLabel') }}</label>
              <input id="entry-payee" v-model="draftPayee" type="text" :class="FIELD_CLASS" />
            </div>
            <div class="col-span-2 flex flex-col gap-[var(--space-hair)]">
              <label :class="LABEL_CLASS" for="entry-memo">{{ t('transactions.detail.memoLabel') }}</label>
              <input
                id="entry-memo"
                v-model="draftMemo"
                type="text"
                :class="FIELD_CLASS"
                :aria-invalid="visibleError(memoError) !== undefined"
                aria-describedby="entry-memo-error"
              />
              <p
                v-if="visibleError(memoError)"
                id="entry-memo-error"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
              >
                {{ memoError }}
              </p>
            </div>
          </section>

          <fieldset class="flex flex-col gap-[var(--space-inline)]">
            <legend
              class="pb-[var(--space-inline)] text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]"
            >
              {{ t('transactions.detail.postingsEditTitle') }}
            </legend>
            <div
              v-for="row in draftRows"
              :key="row.key"
              class="flex flex-col gap-[var(--space-inline)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] p-[var(--space-cell)]"
            >
              <AccountPicker
                :id="`posting-account-${row.key}`"
                :label="t('transactions.detail.accountLabel')"
                :model-value="row.accountId === '' ? undefined : row.accountId"
                include-system
                include-contacts
                @update:model-value="(value) => value !== null && chooseAccount(row, value)"
              />
              <p
                v-if="visibleError(rowErrors(row).account)"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
              >
                {{ rowErrors(row).account }}
              </p>
              <div class="grid grid-cols-[minmax(0,1fr)_6rem] items-start gap-[var(--space-inline)]">
                <AmountInput
                  v-model="row.amount"
                  :currency="rowCurrency(row)"
                  :locale="currentLocale"
                  allow-negative
                  :show-total="false"
                  required
                  :aria-label="t('transactions.detail.amountLabel')"
                  :aria-invalid="visibleError(rowErrors(row).amount) !== undefined"
                />
                <input
                  v-model="row.currency"
                  type="text"
                  maxlength="3"
                  required
                  :aria-label="t('transactions.detail.currencyLabel')"
                  :aria-invalid="visibleError(rowErrors(row).currency) !== undefined"
                  :class="[FIELD_CLASS, 'uppercase']"
                />
              </div>
              <p
                v-if="visibleError(rowErrors(row).amount)"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
              >
                {{ rowErrors(row).amount }}
              </p>
              <p
                v-if="visibleError(rowErrors(row).currency)"
                class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]"
              >
                {{ rowErrors(row).currency }}
              </p>
              <div class="flex items-center justify-between gap-[var(--space-cell)]">
                <input
                  v-model="row.memo"
                  type="text"
                  :placeholder="t('transactions.detail.postingMemoLabel')"
                  :aria-label="t('transactions.detail.postingMemoLabel')"
                  class="min-h-[var(--density-target-min)] min-w-0 flex-1 bg-transparent text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] placeholder:text-[color:var(--color-text-muted)]"
                />
                <button
                  type="button"
                  :disabled="draftRows.length <= 2"
                  class="shrink-0 text-[length:var(--text-step-1)] text-[color:var(--color-danger)] disabled:opacity-60"
                  @click="removeRow(row.key)"
                >
                  {{ t('transactions.detail.removePosting') }}
                </button>
              </div>
            </div>
            <div>
              <button type="button" :class="BUTTON_CLASS" @click="addRow">
                {{ t('transactions.detail.addPosting') }}
              </button>
            </div>
          </fieldset>

          <section
            class="flex flex-col gap-[var(--space-inline)] border-t border-[color:var(--color-border)] pt-[var(--space-block)]"
          >
            <h3 class="text-[length:var(--text-step-2)] font-semibold text-[color:var(--color-text-muted)]">
              {{ t('transactions.detail.detailsTitle') }}
            </h3>
            <div class="flex flex-col gap-[var(--space-hair)]">
              <label :class="LABEL_CLASS" for="entry-notes">{{ t('transactions.detail.notesLabel') }}</label>
              <textarea
                id="entry-notes"
                v-model="draftNotes"
                rows="2"
                class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-[var(--space-inline)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
              />
            </div>
            <div class="flex flex-col gap-[var(--space-hair)]">
              <label :class="LABEL_CLASS" for="entry-tags">{{ t('transactions.detail.tagsLabel') }}</label>
              <input id="entry-tags" v-model="draftTags" type="text" :class="FIELD_CLASS" />
            </div>
          </section>

          <StatusTag v-if="saveErrorPending" tone="neutral">
            {{ t('transactions.detail.pendingRetry') }}
          </StatusTag>
          <StatusTag v-else-if="saveErrorDetail" tone="danger">{{ saveErrorDetail }}</StatusTag>

          <div
            v-if="pendingLeave"
            role="alertdialog"
            aria-labelledby="discard-title"
            aria-describedby="discard-consequence"
            class="sticky bottom-0 -mx-[var(--space-block)] -mb-[var(--space-block)] flex flex-col gap-[var(--space-inline)] border-t border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-block)] py-[var(--space-group)]"
          >
            <h3
              id="discard-title"
              class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]"
            >
              {{ t('transactions.detail.discardTitle') }}
            </h3>
            <p
              id="discard-consequence"
              class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
            >
              {{ t('transactions.detail.discardConsequence') }}
            </p>
            <div class="flex justify-end gap-[var(--space-inline)]">
              <button ref="keepEditingButton" type="button" :class="BUTTON_CLASS" @click="keepEditing">
                {{ t('transactions.detail.keepEditing') }}
              </button>
              <button
                type="button"
                :class="[BUTTON_CLASS, 'text-[color:var(--color-danger)]']"
                @click="discardChanges"
              >
                {{ t('transactions.detail.discardAction') }}
              </button>
            </div>
          </div>
          <div
            v-else
            class="sticky bottom-0 -mx-[var(--space-block)] -mb-[var(--space-block)] flex justify-end gap-[var(--space-inline)] border-t border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-block)] py-[var(--space-group)]"
          >
            <button type="button" :class="BUTTON_CLASS" @click="emit('cancelEdit')">
              {{ t('transactions.detail.cancelEdit') }}
            </button>
            <ActionButton type="submit" :disabled="isSaving">
              {{ isSaving ? t('transactions.detail.saving') : t('transactions.detail.saveAction') }}
            </ActionButton>
          </div>
        </fieldset>
      </form>
    </template>
  </div>
</template>
